import React, { useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Alert, Btn, FormInput } from '../components/UI';
import api from '../api/client';

/* ── 6-box OTP Input ── */
function OTPInput({ value, onChange }) {
  const inputs = useRef([]);
  const digits  = value.split('');

  function handleKey(i, e) {
    if (e.key === 'Backspace') {
      const next = [...digits];
      if (next[i]) { next[i] = ''; onChange(next.join('')); }
      else if (i > 0) inputs.current[i - 1]?.focus();
      return;
    }
    if (e.key === 'ArrowLeft'  && i > 0) { inputs.current[i - 1]?.focus(); return; }
    if (e.key === 'ArrowRight' && i < 5) { inputs.current[i + 1]?.focus(); return; }
  }

  function handleChange(i, e) {
    const val = e.target.value.replace(/\D/g, '').slice(-1);
    if (!val) return;
    const next = [...digits];
    next[i] = val;
    onChange(next.join(''));
    if (i < 5) inputs.current[i + 1]?.focus();
  }

  function handlePaste(e) {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    onChange(pasted.padEnd(6, '').slice(0, 6));
    inputs.current[Math.min(pasted.length, 5)]?.focus();
  }

  return (
    <div style={{ display: 'flex', gap: 8, justifyContent: 'center', margin: '20px 0' }}>
      {[0,1,2,3,4,5].map(i => (
        <input key={i} ref={el => inputs.current[i] = el}
          type="text" inputMode="numeric" maxLength={1}
          value={digits[i] || ''}
          onChange={e => handleChange(i, e)}
          onKeyDown={e => handleKey(i, e)}
          onPaste={handlePaste}
          style={{
            width: 44, height: 52, textAlign: 'center',
            fontSize: 22, fontWeight: 700,
            border: `2px solid ${digits[i] ? '#4f6ef7' : '#e2e6f0'}`,
            borderRadius: 10, outline: 'none',
            background: digits[i] ? '#eef1ff' : '#fff',
            color: '#1a1f5e', transition: 'all .15s', caretColor: 'transparent',
            fontFamily: "'Sora',sans-serif",
          }}
          onFocus={e  => e.target.style.borderColor = '#4f6ef7'}
          onBlur={e   => e.target.style.borderColor = digits[i] ? '#4f6ef7' : '#e2e6f0'}
        />
      ))}
    </div>
  );
}

export default function Login() {
  const { login }  = useAuth();
  const navigate   = useNavigate();

  const [form, setForm]     = useState({ email: '', password: '' });
  const [error, setError]   = useState('');
  const [loading, setLoading] = useState(false);
  const [showPass, setShowPass] = useState(false);

  /* OTP verification state — shown when login returns email_not_verified */
  const [otpScreen,  setOtpScreen]  = useState(false);
  const [otp,        setOtp]        = useState('');
  const [otpError,   setOtpError]   = useState('');
  const [otpLoading, setOtpLoading] = useState(false);
  const [resending,  setResending]  = useState(false);
  const [resendMsg,  setResendMsg]  = useState('');
  const [countdown,  setCountdown]  = useState(0);

  const handle = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  /* Countdown for resend button */
  React.useEffect(() => {
    if (countdown <= 0) return;
    const t = setInterval(() => setCountdown(c => c - 1), 1000);
    return () => clearInterval(t);
  }, [countdown]);

  async function submitLogin(e) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const user = await login(form.email, form.password);
      navigate(user.role === 'admin' ? '/admin' : '/dashboard');
    } catch (err) {
      const data = err.response?.data;
      if (data?.email_not_verified) {
        /* Show OTP screen instead of error */
        setOtpScreen(true);
        setCountdown(60);
        /* Trigger a resend immediately so they get a fresh code */
        try {
          await api.post('/auth/resend-verification', { email: form.email });
        } catch { /* ignore */ }
      } else if (!err.response) {
        /* Network error / server unreachable — give a clear message */
        setError('Could not reach the server. Please check your connection and try again.');
      } else {
        setError(data?.error || 'Login failed. Check your credentials.');
      }
    } finally {
      setLoading(false);
    }
  }

  async function submitOTP(e) {
    e.preventDefault();
    if (otp.length < 6) { setOtpError('Please enter all 6 digits.'); return; }
    setOtpError(''); setOtpLoading(true);
    try {
      const { data } = await api.post('/auth/verify-otp', { email: form.email, otp });
      /* Auto login after verification */
      localStorage.setItem('ef_token', data.token);
      localStorage.setItem('ef_user',  JSON.stringify(data.user));
      navigate(data.user.role === 'admin' ? '/admin' : '/dashboard');
    } catch (err) {
      const data = err.response?.data;
      setOtpError(data?.error || 'Invalid code. Please try again.');
      if (data?.expired) { setOtp(''); setCountdown(0); }
    } finally {
      setOtpLoading(false);
    }
  }

  async function resendOTP() {
    setResending(true); setResendMsg(''); setOtpError('');
    try {
      await api.post('/auth/resend-verification', { email: form.email });
      setResendMsg('New code sent! Check your inbox.');
      setOtp('');
      setCountdown(60);
    } catch {
      setOtpError('Failed to resend. Please try again.');
    } finally {
      setResending(false);
    }
  }

  /* ── OTP Screen ── */
  if (otpScreen) {
    return (
      <div style={{ minHeight: '100vh', background: '#f8f9fc', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, fontFamily: "'DM Sans',sans-serif" }}>
        <div style={{ background: '#fff', borderRadius: 24, padding: '48px 36px', maxWidth: 420, width: '100%', textAlign: 'center', boxShadow: '0 4px 32px rgba(15,18,51,.1)', border: '1px solid #e2e6f0' }}>

          {/* Logo */}
          <div style={{ fontFamily: "'Sora',sans-serif", fontSize: 22, fontWeight: 700, color: '#1a1f5e', marginBottom: 24 }}>
            Qu<span style={{ color: '#4f6ef7' }}>vera</span>
          </div>

          {/* Icon */}
          <div style={{ width: 68, height: 68, borderRadius: '50%', background: '#eef1ff', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
            <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="#4f6ef7" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
              <polyline points="22,6 12,13 2,6"/>
            </svg>
          </div>

          <h2 style={{ fontFamily: "'Sora',sans-serif", fontSize: 20, fontWeight: 700, color: '#1a1f5e', marginBottom: 8 }}>
            Verify your email
          </h2>
          <p style={{ fontSize: 14, color: '#636b89', lineHeight: 1.7, marginBottom: 4 }}>
            We sent a 6-digit code to
          </p>
          <p style={{ fontSize: 15, fontWeight: 700, color: '#1a1f5e', marginBottom: 4 }}>
            {form.email}
          </p>
          <p style={{ fontSize: 13, color: '#9099b4', marginBottom: 16 }}>
            Enter the code below to verify and log in
          </p>

          {otpError && (
            <div style={{ background: '#fee2e2', color: '#dc2626', borderRadius: 10, padding: '10px 14px', fontSize: 13, marginBottom: 12 }}>
              {otpError}
            </div>
          )}
          {resendMsg && (
            <div style={{ background: '#dcfce7', color: '#16a34a', borderRadius: 10, padding: '10px 14px', fontSize: 13, marginBottom: 12 }}>
              {resendMsg}
            </div>
          )}

          <form onSubmit={submitOTP}>
            <OTPInput value={otp} onChange={setOtp} />
            <Btn loading={otpLoading} type="submit"
              style={{ width: '100%', justifyContent: 'center', padding: '13px', fontSize: 15, marginBottom: 16 }}>
              Verify & Log In →
            </Btn>
          </form>

          <div style={{ fontSize: 13, color: '#9099b4' }}>
            Didn't receive the code?{' '}
            {countdown > 0 ? (
              <span>Resend in {countdown}s</span>
            ) : (
              <button type="button" onClick={resendOTP} disabled={resending}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#4f6ef7', fontWeight: 600, fontSize: 13, padding: 0 }}>
                {resending ? 'Sending…' : 'Resend code'}
              </button>
            )}
          </div>

          <button type="button" onClick={() => { setOtpScreen(false); setOtp(''); setOtpError(''); }}
            style={{ marginTop: 20, background: 'none', border: 'none', cursor: 'pointer', color: '#9099b4', fontSize: 13 }}>
            ← Back to login
          </button>
        </div>
      </div>
    );
  }

  /* ── Login Form ── */
  return (
    <div style={{ display: 'flex', minHeight: '100vh', fontFamily: "'DM Sans',sans-serif" }}>
      <style>{`@media(max-width:768px){.login-left{display:none!important;}.login-right{padding:36px 24px!important;}}`}</style>

      {/* Left panel */}
      <div className="login-left" style={{ flex: 1, background: 'linear-gradient(140deg,#1a1f5e,#1e2878)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '60px 48px' }}>
        <div style={{ maxWidth: 400 }}>
          <div style={{ fontFamily: "'Sora',sans-serif", fontSize: 28, fontWeight: 700, color: '#fff', marginBottom: 36 }}>
            Qu<span style={{ color: '#4f6ef7' }}>vera</span>
          </div>
          <h2 style={{ fontFamily: "'Sora',sans-serif", fontSize: 32, fontWeight: 700, color: '#fff', marginBottom: 12 }}>Welcome back.</h2>
          <p style={{ color: 'rgba(255,255,255,.65)', fontSize: 15, lineHeight: 1.7, marginBottom: 40 }}>
            Your earnings are growing while you're away. Log in to check your balance.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {[['$27M+','Total paid out'],['2,100+','Active users'],['98.2%','On-time payouts']].map(([v,l]) => (
              <div key={l} style={{ background: 'rgba(255,255,255,.08)', borderRadius: 12, padding: '16px 20px', border: '1px solid rgba(255,255,255,.1)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <strong style={{ fontFamily: "'Sora',sans-serif", fontSize: 22, fontWeight: 700, color: '#fff' }}>{v}</strong>
                <span style={{ fontSize: 13, color: 'rgba(255,255,255,.55)' }}>{l}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right panel */}
      <div className="login-right" style={{ flex: 1, background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '60px 48px' }}>
        <div style={{ width: '100%', maxWidth: 400 }}>

          {/* Mobile logo */}
          <div style={{ display: 'none', fontFamily: "'Sora',sans-serif", fontSize: 22, fontWeight: 700, color: '#1a1f5e', marginBottom: 28, textAlign: 'center' }} className="mobile-logo">
            Qu<span style={{ color: '#4f6ef7' }}>vera</span>
            <style>{`.mobile-logo{display:none;}@media(max-width:768px){.mobile-logo{display:block!important;}}`}</style>
          </div>

          <h1 style={{ fontFamily: "'Sora',sans-serif", fontSize: 26, fontWeight: 700, color: '#1a1f5e', marginBottom: 6 }}>Sign in</h1>
          <p style={{ fontSize: 14, color: '#9099b4', marginBottom: 28 }}>Enter your credentials to access your dashboard</p>

          <Alert type="error" message={error} />

          <form onSubmit={submitLogin}>
            <FormInput label="Email address" type="email" placeholder="you@example.com"
              value={form.email} onChange={handle('email')} required />

            {/* Password with show/hide */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#2e3456', marginBottom: 6 }}>Password</label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showPass ? 'text' : 'password'}
                  placeholder="Your password"
                  value={form.password}
                  onChange={handle('password')}
                  required
                  style={{ width: '100%', padding: '11px 44px 11px 14px', border: '1.5px solid #e2e6f0', borderRadius: 10, fontSize: 14, color: '#0f1233', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' }}
                />
                <button type="button" onClick={() => setShowPass(v => !v)}
                  style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: '#9099b4', display: 'flex', alignItems: 'center' }}>
                  {showPass ? (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24"/>
                      <line x1="1" y1="1" x2="23" y2="23"/>
                    </svg>
                  ) : (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                      <circle cx="12" cy="12" r="3"/>
                    </svg>
                  )}
                </button>
              </div>
            </div>

            <div style={{ textAlign: 'right', marginTop: -8, marginBottom: 16 }}>
              <Link to="/forgot-password" style={{ fontSize: 13, color: '#4f6ef7', textDecoration: 'none', fontWeight: 500 }}>
                Forgot password?
              </Link>
            </div>

            <Btn loading={loading} type="submit" style={{ width: '100%', justifyContent: 'center', padding: 13, fontSize: 15 }}>
              Log In →
            </Btn>
          </form>

          <p style={{ fontSize: 14, color: '#9099b4', textAlign: 'center', marginTop: 20 }}>
            Don't have an account? <Link to="/signup" style={{ color: '#4f6ef7', fontWeight: 600 }}>Sign up free</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
